import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/takeUntil';
import {
  ClothingBiometry,
  ClothingRequest,
  EmployeeClothingEntitlementsSource,
  EmployeeClothingOverview,
  HeadClothingManualExtensionPayload,
  StorekeeperIssuePayload,
  StorekeeperRejectNoStockPayload
} from '../../../models/clothing.model';
import { ProfileClothingUnitItem, ProfileClothingViewItem } from '../../../models/profile-clothing.model';
import { AppConstants } from './app.constants';
import {
  ClothesInfo,
  ViewUserProfileClothingContext,
  ViewUserProfileClothingStateView
} from './view-user-profile.types';
import {
  PROFILE_CLOTHING_MESSAGE_KEYS,
  PROFILE_EMPTY_TEXT,
  PROFILE_FIRST_RESPONSE_INDEX,
  PROFILE_FORBIDDEN_STATUS,
  PROFILE_SECOND_RESPONSE_INDEX,
  PROFILE_SPACE
} from './view-user-profile.constants';
import { ViewUserProfileClothingStore } from './view-user-profile-clothing.store';
import { ViewUserProfileClothingGateway } from './view-user-profile-clothing.gateway';
import { ViewUserProfileClothingUi } from './view-user-profile-clothing.ui';

const CLOTHING_ISSUED_STATUS = 'ISSUED';
const CLOTHING_REJECTED_STATUS = 'REJECTED_NO_STOCK';

@Injectable()
export class ViewUserProfileClothingCoordinator implements OnDestroy {
  private context: ViewUserProfileClothingContext;
  private destroy$ = new Subject<void>();
  private contextChanged$ = new Subject<void>();

  constructor(
    private gateway: ViewUserProfileClothingGateway,
    private ui: ViewUserProfileClothingUi,
    private state: ViewUserProfileClothingStore
  ) {}

  viewState(): ViewUserProfileClothingStateView {
    return this.state;
  }

  load(context: ViewUserProfileClothingContext): void {
    this.contextChanged$.next();
    this.state.failHeadLoad(true);
    this.state.failStorekeeperLoad(true);
    this.state.clearLegacyClothes();
    this.context = context;
    this.state.setProfile(context.user);
    this.loadHeadClothing();
    this.loadStorekeeperClothing();
  }

  ngOnDestroy(): void {
    this.contextChanged$.next();
    this.contextChanged$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  reload(): void { if (this.context) { this.load(this.context); } }

  loadLegacyClothes(userId: string): void {
    this.gateway.legacyClothes(userId).takeUntil(this.contextChanged$).takeUntil(this.destroy$).subscribe(
      (response) => this.handleLegacyClothesSuccess(response),
      () => this.state.clearLegacyClothes()
    );
  }

  openBiometrics(onSuccess: () => void): void {
    this.ui.openBiometrics(this.context.user, onSuccess);
  }

  openClothes(onSuccess: () => void): void {
    this.ui.openClothes(this.context.user, onSuccess);
  }

  hasActions(item: ProfileClothingViewItem): boolean {
    return this.hasStorekeeperActions(item) || this.state.hasHeadActions(item);
  }

  hasStorekeeperActions(item: ProfileClothingViewItem): boolean {
    return this.showIssueAction(item) || this.showRejectAction(item);
  }

  showIssueAction(item: ProfileClothingViewItem): boolean {
    return this.state.showStorekeeperClothing && !!item && item.canIssue;
  }

  showRejectAction(item: ProfileClothingViewItem): boolean {
    return this.state.showStorekeeperClothing && !this.context.isAdmin && !!item && item.hasActiveRequest;
  }

  issueStorekeeperItem(item: ProfileClothingViewItem): void {
    if (item.issueDisabled || this.state.isIssuePending(item)) {
      return;
    }
    this.openIssueModal(item);
  }

  rejectStorekeeperRequest(item: ProfileClothingViewItem): void {
    if (item.rejectDisabled || this.state.isRejectPending(item)) {
      return;
    }
    this.state.beginReject(item);
    this.gateway.reject(item.requestId, this.rejectPayload())
      .takeUntil(this.destroy$).subscribe(
      (response) => this.handleRejectResponse(response),
      (error) => this.handleRejectError(error)
    );
  }

  extendHeadPosition(item: ProfileClothingViewItem, unit?: ProfileClothingUnitItem): void {
    if (this.isHeadExtendDisabled(item, unit) || this.state.isHeadExtendPending(item, unit)) {
      return;
    }
    this.openHeadExtensionModal(item, unit);
  }

  private loadStorekeeperClothing(): void {
    if (!this.context.isStorekeeper && !this.context.isAdmin && !this.context.isHead) {
      return;
    }
    this.state.beginStorekeeperLoad();
    Observable.forkJoin(
      this.getClothingOverviewForIssue(),
      this.getClothingEntitlementsForIssue()
    ).takeUntil(this.contextChanged$).takeUntil(this.destroy$).subscribe(
      (response) => this.handleStorekeeperOverviewSuccess(response),
      (error) => this.state.failStorekeeperLoad(this.isForbidden(error))
    );
  }

  private handleStorekeeperOverviewSuccess(response: Object[]): void {
    var overview = response[PROFILE_FIRST_RESPONSE_INDEX] as EmployeeClothingOverview;
    var entitlements = response[PROFILE_SECOND_RESPONSE_INDEX] as EmployeeClothingEntitlementsSource;
    this.state.completeStorekeeperLoad(
      overview,
      this.gateway.storekeeperItems(overview, entitlements),
      this.resolveStorekeeperBiometry(overview, entitlements)
    );
  }

  private loadHeadClothing(): void {
    if (!this.context.isHead) {
      return;
    }
    this.state.beginHeadLoad();
    this.gateway.headPositions(this.context.userId)
      .takeUntil(this.contextChanged$).takeUntil(this.destroy$).subscribe(
      (positions) => this.state.completeHeadLoad(
        this.gateway.headItems(positions)
      ),
      (error) => this.state.failHeadLoad(this.isForbidden(error))
    );
  }

  private handleIssueSuccess(): void {
    this.ui.success(PROFILE_CLOTHING_MESSAGE_KEYS.ISSUE_SUCCESS);
    this.loadStorekeeperClothing();
    this.loadHeadClothing();
  }

  private handleIssueResponse(response: ClothingRequest): void {
    if (response.status === CLOTHING_ISSUED_STATUS) {
      this.handleIssueSuccess();
      return;
    }
    this.loadStorekeeperClothing();
  }

  private handleRejectResponse(response: ClothingRequest): void {
    if (response.status === CLOTHING_REJECTED_STATUS) {
      this.handleRejectSuccess();
      return;
    }
    this.loadStorekeeperClothing();
  }

  private handleRejectSuccess(): void {
    this.ui.success(PROFILE_CLOTHING_MESSAGE_KEYS.REJECT_SUCCESS);
    this.loadStorekeeperClothing();
    this.loadHeadClothing();
  }

  private handleHeadExtensionSuccess(): void {
    this.ui.success(PROFILE_CLOTHING_MESSAGE_KEYS.EXTENSION_SUCCESS);
    this.loadStorekeeperClothing();
    this.loadHeadClothing();
  }

  private handleIssueError(error: HttpErrorResponse): void {
    this.state.failIssue();
    this.showError(error, PROFILE_CLOTHING_MESSAGE_KEYS.ISSUE_ERROR);
  }

  private handleRejectError(error: HttpErrorResponse): void {
    this.state.failReject();
    this.showError(error, PROFILE_CLOTHING_MESSAGE_KEYS.REJECT_ERROR);
  }

  private handleHeadExtensionError(error: HttpErrorResponse): void {
    this.state.failHeadExtension();
    this.showError(error, PROFILE_CLOTHING_MESSAGE_KEYS.EXTENSION_ERROR);
  }

  private showError(error: HttpErrorResponse, fallbackKey: string): void {
    this.ui.error(error, fallbackKey);
  }

  private resolveStorekeeperBiometry(
    overview: EmployeeClothingOverview,
    entitlementsSource: EmployeeClothingEntitlementsSource
  ): ClothingBiometry {
    if (overview && overview.biometry) {
      return overview.biometry;
    }
    if (!entitlementsSource || Array.isArray(entitlementsSource)) {
      return null;
    }
    return entitlementsSource.biometry || null;
  }

  private issueStorekeeperItemByMode(
    item: ProfileClothingViewItem,
    payload: StorekeeperIssuePayload
  ): void {
    if (this.context.isAdmin) {
      this.directIssueAdminItem(item, payload);
    } else if (item.issueByRequest) {
      this.issueStorekeeperRequest(item, payload);
    } else {
      this.directIssueStorekeeperItem(item, payload);
    }
  }

  private issueStorekeeperRequest(item: ProfileClothingViewItem, payload: StorekeeperIssuePayload): void {
    this.gateway.issueRequest(item.requestId, payload)
      .takeUntil(this.destroy$).subscribe(
      (response) => this.handleIssueResponse(response),
      (error) => this.handleIssueError(error)
    );
  }

  private directIssueStorekeeperItem(item: ProfileClothingViewItem, payload: StorekeeperIssuePayload): void {
    this.gateway.issueStorekeeper(
      this.context.userId,
      item.catalogItemId,
      payload
    ).takeUntil(this.destroy$).subscribe(
      () => this.handleIssueSuccess(),
      (error) => this.handleIssueError(error)
    );
  }

  private directIssueAdminItem(item: ProfileClothingViewItem, payload: StorekeeperIssuePayload): void {
    this.gateway.issueAdmin(
      this.context.userId,
      item.catalogItemId,
      payload
    ).takeUntil(this.destroy$).subscribe(
      () => this.handleIssueSuccess(),
      (error) => this.handleIssueError(error)
    );
  }

  private getClothingOverviewForIssue(): Observable<EmployeeClothingOverview> {
    return this.context.isAdmin
      ? this.gateway.overview(this.context.userId, true)
      : this.gateway.overview(this.context.userId, false);
  }

  private getClothingEntitlementsForIssue(): Observable<EmployeeClothingEntitlementsSource> {
    return this.context.isAdmin
      ? this.gateway.entitlements(this.context.userId, true)
      : this.gateway.entitlements(this.context.userId, false);
  }

  private openIssueModal(item: ProfileClothingViewItem): void {
    this.ui.openIssue(
      item,
      this.issueEmployeeName(),
      (payload: StorekeeperIssuePayload) => this.submitStorekeeperIssue(item, payload)
    );
  }

  private submitStorekeeperIssue(item: ProfileClothingViewItem, payload: StorekeeperIssuePayload): void {
    if (!payload) {
      return;
    }
    this.state.beginIssue(item);
    this.issueStorekeeperItemByMode(item, payload);
  }

  private openHeadExtensionModal(item: ProfileClothingViewItem, unit?: ProfileClothingUnitItem): void {
    this.ui.openHeadExtension(
      item,
      unit,
      (payload: HeadClothingManualExtensionPayload) => this.submitHeadExtension(item, payload, unit)
    );
  }

  private submitHeadExtension(
    item: ProfileClothingViewItem,
    payload: HeadClothingManualExtensionPayload,
    unit?: ProfileClothingUnitItem
  ): void {
    if (!payload) {
      return;
    }
    this.state.beginHeadExtension(item, unit);
    this.gateway.extend(item.positionId, payload)
      .takeUntil(this.destroy$).subscribe(
      () => this.handleHeadExtensionSuccess(),
      (error) => this.handleHeadExtensionError(error)
    );
  }

  private issueEmployeeName(): string {
    if (!this.context.user) {
      return PROFILE_EMPTY_TEXT;
    }
    return [this.context.user.lastName, this.context.user.firstName, this.context.user.patronymic]
      .filter((value) => !!value)
      .join(PROFILE_SPACE);
  }

  private rejectPayload(): StorekeeperRejectNoStockPayload {
    return { description: PROFILE_EMPTY_TEXT };
  }

  private isHeadExtendDisabled(item: ProfileClothingViewItem, unit?: ProfileClothingUnitItem): boolean {
    return unit ? unit.headExtendDisabled : item.headExtendDisabled;
  }

  private isForbidden(error: HttpErrorResponse): boolean {
    return !!error && error.status === PROFILE_FORBIDDEN_STATUS;
  }

  private handleLegacyClothesSuccess(response: HttpResponse<ClothesInfo>): void {
    const info = response.body;
    const status = info.clothesStatus;
    if (AppConstants.CLOTHES_STATUS[status]) {
      info.clothesStatus = AppConstants.CLOTHES_STATUS[status];
    }
    this.state.setLegacyClothes(info);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-user-profile/view-user-profile-clothing.coordinator.ts